// 路由与主题：深色"指挥台"AntD 主题 + 查询缓存 + 懒加载页面
import { Suspense, lazy } from 'react';
import type { ComponentProps, ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { App as AntApp, ConfigProvider, Spin, theme as antTheme } from 'antd';
import enUS from 'antd/locale/en_US';
import zhCN from 'antd/locale/zh_CN';
import zhTW from 'antd/locale/zh_TW';
import { useTranslation } from 'react-i18next';
import { useAuth } from './auth/AuthContext';
import { Shell } from './layouts/Shell';
import { ErrorBoundary } from './components/ErrorBoundary';

type AntdLocale = ComponentProps<typeof ConfigProvider>['locale'];

const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Events = lazy(() => import('./pages/Events'));
const EventDetail = lazy(() => import('./pages/EventDetail'));
const Services = lazy(() => import('./pages/Services'));
const Customers = lazy(() => import('./pages/Customers'));
const Categories = lazy(() => import('./pages/Categories'));
const Users = lazy(() => import('./pages/Users'));
const Audit = lazy(() => import('./pages/Audit'));
const Settings = lazy(() => import('./pages/Settings'));
const MyEvents = lazy(() => import('./pages/mobile/MyEvents'));
const MobileEvent = lazy(() => import('./pages/mobile/MobileEvent'));

const ANTD_LOCALES: Record<string, AntdLocale> = { 'zh-CN': zhCN, 'zh-TW': zhTW, en: enUS };

const themeConfig = {
  algorithm: antTheme.darkAlgorithm,
  token: {
    colorPrimary: '#3b82f6',
    colorBgBase: '#0b1220',
    colorBgContainer: '#101a2e',
    colorBgElevated: '#16233c',
    colorBorder: '#22314f',
    colorBorderSecondary: '#1a2743',
    colorText: '#e8eefc',
    colorTextSecondary: '#93a4c4',
    borderRadius: 10,
    fontFamily: "'Segoe UI Variable Text','Segoe UI','Microsoft YaHei','PingFang SC','Noto Sans CJK SC',sans-serif",
  },
  components: {
    Layout: { siderBg: 'transparent', headerBg: 'transparent', bodyBg: 'transparent' },
    Menu: { darkItemBg: 'transparent', darkSubMenuItemBg: 'transparent', darkItemSelectedBg: 'rgba(59,130,246,.22)' },
    Card: { colorBgContainer: '#101a2e' },
    Table: { headerBg: '#16233c', rowHoverBg: 'rgba(59,130,246,.08)' },
  },
};

function Guard({ children }: { children: ReactNode }) {
  const { user, booting } = useAuth();
  if (booting) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <Shell>{children}</Shell>;
}

function Fallback() {
  return <div style={{ padding: 80, textAlign: 'center' }}><Spin size="large" /></div>;
}

export default function App() {
  const { i18n } = useTranslation();
  const locale = ANTD_LOCALES[i18n.language] ?? enUS;
  return (
    <ConfigProvider locale={locale} theme={themeConfig}>
      <AntApp>
        <Suspense fallback={<Fallback />}>
          <ErrorBoundary>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Guard><Dashboard /></Guard>} />
            <Route path="/events" element={<Guard><Events /></Guard>} />
            <Route path="/events/:uuid" element={<Guard><EventDetail /></Guard>} />
            <Route path="/services" element={<Guard><Services /></Guard>} />
            <Route path="/customers" element={<Guard><Customers /></Guard>} />
            <Route path="/categories" element={<Guard><Categories /></Guard>} />
            <Route path="/users" element={<Guard><Users /></Guard>} />
            <Route path="/audit" element={<Guard><Audit /></Guard>} />
            <Route path="/settings" element={<Guard><Settings /></Guard>} />
            <Route path="/m" element={<Guard><MyEvents /></Guard>} />
            <Route path="/m/event/:uuid" element={<Guard><MobileEvent /></Guard>} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          </ErrorBoundary>
        </Suspense>
      </AntApp>
    </ConfigProvider>
  );
}
